// Capa HTTP de las plantillas de informe y del maestro de ítems. Tipos +
// funciones fetch; el estado vive en TanStack Query (`hooks/usePlantillas.ts`)
// y la estructura en edición en `lib/borradorDePlantilla.ts`.
//
// El API envuelve todo en `data`. Los valores de `tipo_contenido` y
// `obligatoriedad` son los del enum del servidor; la etiqueta con que se
// muestran el tipo se lee de `tipo_contenido_etiqueta` cuando el recurso la
// manda, y de `ETIQUETA_DE_TIPO` cuando el tipo se arma en el navegador (un
// ítem recién creado todavía no pasó por el servidor).

import { apiFetch } from "./api";
import type { Obligatoriedad } from "./estructuraDePlantilla";
import type { ModoDeProduccion, RecursoDelDocumento } from "./produccionDelItem";

export type TipoDeContenido =
  | "texto"
  | "cuadro_de_valor"
  | "tabla"
  | "imagen"
  | "imagen_mapa"
  | "firma"
  | "documento_anexo";

export const TIPOS_DE_CONTENIDO: { valor: TipoDeContenido; etiqueta: string }[] = [
  { valor: "texto", etiqueta: "Texto" },
  { valor: "cuadro_de_valor", etiqueta: "Cuadro de valor" },
  { valor: "tabla", etiqueta: "Tabla" },
  { valor: "imagen", etiqueta: "Imagen" },
  { valor: "imagen_mapa", etiqueta: "Imagen / mapa" },
  { valor: "firma", etiqueta: "Firma" },
  { valor: "documento_anexo", etiqueta: "Documento anexo" },
];

export function etiquetaDeTipo(tipo: TipoDeContenido): string {
  return TIPOS_DE_CONTENIDO.find((t) => t.valor === tipo)?.etiqueta ?? tipo;
}

export const ETIQUETA_DE_OBLIGATORIEDAD: Record<Obligatoriedad, string> = {
  obligatorio: "Obligatorio",
  condicional: "Condicional",
  opcional: "Opcional",
};

/** Cuántos informes se analizaron para medir la `frecuencia` de cada ítem. */
export const INFORMES_ANALIZADOS = 80;

/**
 * La línea de respaldo de una plantilla en el listado, con la redacción del diseño.
 * Concuerda en número —"1 informe analizado"— y una plantilla sin informes que la
 * respalden (creada en la app, `null`) se presenta como nueva en vez de omitirse.
 */
export function respaldoDeLaPlantilla(informesAnalizados: number | null | undefined): string {
  if (!informesAnalizados) return "Plantilla nueva";
  const plural = informesAnalizados > 1 ? "s" : "";
  return `Respaldada por ${informesAnalizados} informe${plural} analizado${plural}`;
}

export interface ResumenDePlantilla {
  id: number;
  codigo: string;
  nombre: string;
  proposito: string | null;
  valor: string | null;
  /** Informes reales que respaldan la plantilla; `null` si no se midió (las creadas en la app). */
  informes_analizados?: number | null;
  orden: number;
  total: number;
  obligatorios: number;
  condicionales: number;
  opcionales: number;
}

export interface DatosDePlantilla {
  id: number;
  codigo: string;
  nombre: string;
  proposito: string | null;
  valor: string | null;
  informes_analizados?: number | null;
  orden: number;
  /** El formato de salida (o los dos, "FS06 / FS07") que se propone al elegir la plantilla. */
  formatos_por_defecto?: { id: number; nombre: string }[];
}

export interface MaestroItem {
  id: number;
  codigo: string;
  grupo: string;
  subgrupo: string | null;
  nombre: string;
  descripcion: string | null;
  tipo_contenido: TipoDeContenido;
  tipo_contenido_etiqueta: string;
  fuente: string | null;
  condicion_sugerida: string | null;
  /** Presencia en los informes analizados, de 0 a `INFORMES_ANALIZADOS`. */
  frecuencia: number;
  ejemplo: string | null;
  orden: number;
  /** "Solo en esta plantilla": todavía no forma parte del maestro. */
  local: boolean;
  /** Cómo se produce al generar el informe; `null` mientras nadie lo confirme. */
  modo_produccion: ModoDeProduccion | null;
  modo_produccion_etiqueta: string | null;
  /** Pestaña de la que sale un «texto ya validado» (`SeccionInforme`). */
  seccion_origen: string | null;
  /** Solo cambia el ORDEN de redacción de un ítem redactado por IA. */
  se_redacta_al_final: boolean;
  /** La redacción estándar de un ítem de «Texto fijo», con marcadores `{{clave}}`. */
  texto_fijo: string | null;
  /** Qué pieza del expediente muestra al armar el documento (sistema, tabla, cálculo, imagen). */
  recurso: RecursoDelDocumento | null;
  recurso_etiqueta: string | null;
  /** Ids de los ítems de los que depende (solo un ítem redactado por IA). */
  dependencias: number[];
  /** Los mismos con su nombre, para mostrarlos sin tener el maestro entero. */
  dependencias_detalle: { id: number; nombre: string }[];
}

/** Un ítem COMPARTIDO del maestro y en qué plantillas se usa. */
export interface MaestroItemConUso extends MaestroItem {
  /** id de plantilla → obligatoriedad. Vacío si ninguna lo usa. */
  uso: Record<string, Obligatoriedad>;
}

export interface FilaDelApi {
  maestro_item_id: number | null;
  grupo: string;
  subgrupo: string | null;
  obligatoriedad: Obligatoriedad | null;
  condicion: string | null;
}

export interface DetalleDePlantilla {
  plantilla: DatosDePlantilla;
  filas: FilaDelApi[];
  items: MaestroItem[];
  /** Recursos que piden dos ítems incluidos (lo guardado); el editor lo recalcula sobre el borrador. */
  recursos_repetidos?: { recurso: string; items: { id: number; nombre: string }[] }[];
}

export interface DatosEditables {
  nombre: string;
  proposito?: string | null;
  valor?: string | null;
}

export type FilaDeEstructura =
  | { grupo: string; subgrupo?: string }
  | {
      maestro_item_id?: number;
      clave?: string;
      grupo: string;
      subgrupo?: string;
      obligatoriedad: Obligatoriedad;
      condicion?: string;
    };

export interface LocalDeEstructura {
  id?: number;
  clave?: string;
  nombre: string;
  descripcion?: string;
  tipo_contenido: TipoDeContenido;
  fuente?: string;
  publicar?: boolean;
}

export interface EstructuraAGuardar {
  filas: FilaDeEstructura[];
  locales: LocalDeEstructura[];
}

export interface ItemDelMaestroEditable {
  grupo: string;
  subgrupo?: string | null;
  nombre: string;
  descripcion?: string | null;
  tipo_contenido: TipoDeContenido;
  fuente?: string | null;
  condicion_sugerida?: string | null;
  modo_produccion?: ModoDeProduccion | null;
  seccion_origen?: string | null;
  se_redacta_al_final?: boolean;
  texto_fijo?: string | null;
  recurso?: RecursoDelDocumento | null;
  dependencias?: number[];
}

/** Cambio de uso por plantilla: `null` saca el ítem de esa plantilla. */
export type CambioDeUso = Record<string, Obligatoriedad | null>;

export interface ContenidoDeLaPlantilla {
  maestro_item_id: number;
  nombre: string;
  descripcion: string | null;
  grupo: string;
  subgrupo: string | null;
  obligatoriedad: Obligatoriedad;
  condicion: string | null;
  tipo_contenido: TipoDeContenido;
}

export async function fetchPlantillas(): Promise<ResumenDePlantilla[]> {
  const { data } = await apiFetch<{ data: ResumenDePlantilla[] }>("/api/plantillas-informe");
  return data;
}

export async function fetchPlantilla(id: number): Promise<DetalleDePlantilla> {
  const { data } = await apiFetch<{ data: DetalleDePlantilla }>(`/api/plantillas-informe/${id}`);
  return data;
}

export async function crearPlantillaRequest(
  input: DatosEditables & { base_id?: number | null },
): Promise<DetalleDePlantilla> {
  const { data } = await apiFetch<{ data: DetalleDePlantilla }>("/api/plantillas-informe", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function actualizarPlantillaRequest(
  id: number,
  input: DatosEditables,
): Promise<DetalleDePlantilla> {
  const { data } = await apiFetch<{ data: DetalleDePlantilla }>(`/api/plantillas-informe/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return data;
}

export async function guardarEstructuraRequest(
  id: number,
  estructura: EstructuraAGuardar,
): Promise<DetalleDePlantilla> {
  const { data } = await apiFetch<{ data: DetalleDePlantilla }>(
    `/api/plantillas-informe/${id}/estructura`,
    { method: "PUT", body: JSON.stringify(estructura) },
  );
  return data;
}

export async function fetchMaestroItems(): Promise<MaestroItemConUso[]> {
  const { data } = await apiFetch<{ data: MaestroItemConUso[] }>("/api/maestro-items");
  return data;
}

export async function crearMaestroItemRequest(
  input: ItemDelMaestroEditable,
): Promise<MaestroItem> {
  const { data } = await apiFetch<{ data: MaestroItem }>("/api/maestro-items", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function actualizarMaestroItemRequest(
  id: number,
  input: ItemDelMaestroEditable & { uso?: CambioDeUso },
): Promise<MaestroItem> {
  const { data } = await apiFetch<{ data: MaestroItem }>(`/api/maestro-items/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return data;
}

/** Los ítems de una plantilla, en orden, para armar un informe. */
export async function fetchContenidosDeLaPlantilla(
  id: number,
): Promise<ContenidoDeLaPlantilla[]> {
  const { data } = await apiFetch<{ data: ContenidoDeLaPlantilla[] }>(
    `/api/plantillas-informe/${id}/contenidos`,
  );
  return data;
}
